import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ChargingPoint } from '../../../../assets/icons/dashboard/charging-point.svg';
import { ReactComponent as NavigateArrow } from '../../../../assets/icons/dashboard/navigate-arrow.svg';
import { ReactComponent as RefreshIcon } from '../../../../assets/icons/dashboard/rotate-left.svg';
import { ReactComponent as LoaderIcon } from '../../../../assets/icons/loader.svg';
import { useAppDispatch } from '../../../../redux/hooks';
import { updateChargingPointFilters } from '../../../../redux/slices/DeviceManagementSlice';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import { updateDeviceFilters } from '../../../../redux/slices/Partners/DeviceManagementSlice';
import { updateChargingPointFilters as updatePersonalChargingPointFilters } from '../../../../redux/slices/PersonalDeviceSlice';
import MyButton from '../../../../shared-components/button/Button';
// import ToggleWithIcons from '../../../shared-components/toggle-with-icons/ToggleWithIcons';
import { TooltipIcon } from '../../../../shared-components/tooltip/TooltipIcon';
import { getRelativeTime } from '../../../../shared-functions/DateTime';
import dashboard from '../../../../utils/api/dashboard';

interface DeviceStatusProperties {
  status: 'Online' | 'Offline' | string;
  count: number;
  last_updated_time: string | number;
}

interface QueryParameters {
  [key: string]: any;
}

interface DeviceStatusChartProperties {
  filters?: {
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  };
}

/**
 * Device status component
 * @param {any} param0 - The param0
 * @param {any} param0.filters - The filters prop
 * @returns {any} The result
 */
export default function DeviceStatus({ filters }: DeviceStatusChartProperties) {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [lastUpdatedTime, setLastUpdatedTime] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [deviceStatusCount, setDeviceStatusCount] = useState<{
    online: number;
    offline: number;
    outOfService: number;
  }>({
    online: 0,
    offline: 0,
    outOfService: 0,
  });

  /**
   * Function to fetch connector status report
   * @param {any} query - The query
   */
  const fetchConnectorStatusReport = (query: QueryParameters) => {
    setIsLoading(true);
    const { facilityIds, stationIds, deviceIds } = filters || {};
    dashboard
      .getDeviceStatusReport({
        ...query,
        accountCustomId: facilityIds,
        stationIds,
        deviceIds,
        type: filters?.type,
      })
      .then(
        (response: {
          data: { finalResult: DeviceStatusProperties[]; last_updated_time: string };
        }) => {
          const result = response.data.finalResult;
          setIsLoading(false);

          const statusMap: { online: number; offline: number; outOfService: number } = {
            online: 0,
            offline: 0,
            outOfService: 0,
          };

          result.forEach((item) => {
            const status = item.status.toLowerCase();
            if (status === 'online') {
              statusMap.online = item.count;
            } else if (status === 'offline') {
              statusMap.offline = item.count;
            } else if (status === 'out of service') {
              statusMap.outOfService = item.count;
            }
          });

          setDeviceStatusCount(statusMap);
          if (response.data?.last_updated_time) {
            setLastUpdatedTime(response.data.last_updated_time);
          }
        },
      )
      .catch((error: any) => {
        setIsLoading(false);
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Function to handle redirect device management page with filter
   * @param {string} key - The status key
   * @returns {void}
   */
  const handleClickPieChartCell = (key: string): void => {
    const filterValue = [key];
    dispatch(
      updateDeviceFilters({
        key: 'deviceStatus',
        value: filterValue,
        type: 'restAndAdd',
      }),
    );
    dispatch(
      updateChargingPointFilters({
        key: 'deviceStatus',
        value: filterValue,
        type: 'restAndAdd',
      }),
    );
    dispatch(
      updatePersonalChargingPointFilters({
        key: 'deviceStatus',
        value: filterValue,
        type: 'restAndAdd',
      }),
    );
    navigate({
      pathname: '/ev-admin/operations/chargers',
      search: `?${new URLSearchParams({
        deviceStatus: key,
        chargerType: filters?.type?.[0] || 'personal',
      })}`,
    });
  };

  useEffect(() => {
    fetchConnectorStatusReport({ hardRefresh: true });
  }, [filters]);

  return (
    <div className="dashboard-device-status-wrap">
      <div className="dashboard-device-status-header">
        <div className="header-title">
          <div className="title-wrapper">
            <h6>{t('deviceStatus')}</h6>
            <TooltipIcon text={t('chargerStatusChart')} infoColor="info" />
          </div>
          <NavigateArrow
            className="header-nav-arrow"
            onClick={() => navigate('/ev-admin/operations/chargers')}
          />
        </div>
      </div>
      <div className="dashboard-device-status-container">
        <div className="device-status-top">
          <div
            className="outer-circle go-green"
            role="presentation"
            onClick={() => handleClickPieChartCell('online')}>
            <div className="inner-circle">{deviceStatusCount?.online}</div>
          </div>
          <div
            className="outer-circle out-of-service"
            role="presentation"
            onClick={() => handleClickPieChartCell('offline')}>
            <div className="inner-circle">{deviceStatusCount?.offline}</div>
          </div>
          <div className="absolute-container">
            <svg
              viewBox="0 0 213 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="none">
              <path
                d="M1.22266 15.9421C9.76059 10.3572 33.8465 -0.315986 61.8869 1.66973C96.9373 4.15187 198.044 37.0397 212.199 23.3879"
                stroke="url(#paint0_linear_device_status)"
                strokeWidth={2}
                vectorEffect="non-scaling-stroke"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_device_status"
                  x1="1.22266"
                  y1="14.0601"
                  x2="212.199"
                  y2="14.0601"
                  gradientUnits="userSpaceOnUse">
                  <stop stopColor="var(--primary-color, #00A79D)" />
                  <stop offset="1" stopColor="var(--available-color, #6EC282)" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>
        <div className="device-status-bottom">
          <div className="outer-circle go-green">
            <div className="inner-circle">
              <ChargingPoint />
              <p>{t('greenGo')}</p>
            </div>
          </div>
          <div className="outer-circle out-of-service">
            <div className="inner-circle">
              <ChargingPoint />
              <p>{t('outOfService')}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="refresh">
        <div className="refresh-icon">
          {isLoading ? (
            <div className="loading">
              <LoaderIcon className="status-loader-icon" />
            </div>
          ) : (
            <MyButton
              label=""
              buttonType="none"
              onClickFunc={() => fetchConnectorStatusReport({ hardRefresh: true })}
              svgBackIcon={<RefreshIcon className="status-refresh-icon" />}
            />
          )}
        </div>
        <p className="updated-time">
          {t('lastUpdatedTime')} : {lastUpdatedTime ? getRelativeTime(lastUpdatedTime, t) : ''}{' '}
        </p>
      </div>
      {/* <div className="device-status-toggle">
        <ToggleWithIcons
          leftMenu={chartToggle[0]}
          rightMenu={chartToggle[1]}
          handleClickToggle={handleToggle}
        />
      </div> */}
    </div>
  );
}

DeviceStatus.defaultProps = {
  filters: {
    facilityIds: [],
    stationIds: [],
    deviceIds: [],
    type: ['commercial'],
  },
};
